export function GridCsvColumnGuide({
  entityLabel,
}: {
  entityLabel: string;
}): JSX.Element {
  return (
    <div className="min-w-0 space-y-3">
      <details className="rounded-md border border-border px-3 py-2 text-xs text-muted-foreground">
        <summary className="cursor-pointer font-medium text-foreground">CSV column guide</summary>
        <div className="mt-3 space-y-3 break-words leading-5">
          {entityLabel === "Feeder" ? (
            <>
              <p>
                <strong className="text-foreground">Required identity and location:</strong>{" "}
                external_key, feeder_name, distribution_company_external_key,
                substation_external_key. Use each existing company's and substation's external key,
                not its display name. Keep imported FDR IDs or existing pilot keys unchanged.
                feeder_name is the canonical saved name; display_name is a compatibility alias,
                not a second stored name. Supply the same cleaned name if both columns are present.
                Original names belong only in the reconciliation workbook, not in source_metadata.
                Keep the full official NERC feeder name; do not shorten it to a terminal code.
              </p>
              <p>
                <strong className="text-foreground">Feeder classification:</strong>{" "}
                feeder_type, feeder_type_detail. Use the supplied classification labels;
                other labels are also accepted.
              </p>
              <p>
                <strong className="text-foreground">Feeder engineering:</strong> feeder_voltage,
                feeder_band, ct_vt_condition, ct_primary, ct_secondary, vt_primary, vt_secondary,
                power_transformer_id, power_transformer_capacity_mva, latitude, longitude.
              </p>
              <p>
                <strong className="text-foreground">Feeder installation:</strong>{" "}
                station_dc_nominal_v, station_dc_min_v, station_dc_max_v, preferred_network,
                installation_type_available, remarks.
                Optional supply hierarchy: upstream_feeder_name, upstream_voltage_kv (incoming feeder),
                source_voltage_kv (transmission supply). voltage_kv is the own feeder voltage.
              </p>
              <p>
                On creation, blank CT/VT components use 1:1 when both are blank. Each blank
                component defaults to 1. This is an unverified default. Enter primary and secondary
                as separate positive whole numbers, not a ratio string. Blank or omitted optional
                fields preserve existing values on CSV update; use the edit form to clear metadata.
              </p>
              <p>
                <strong className="text-foreground">Standardized metadata:</strong>{" "}
                display_name, voltage_kv (numeric kV), served_states, regulators,
                nerc_service_classes (semicolon-separated lists). Preserve all applicable values.
                feeder_location and major_streets retain source context. metered_customers and
                unmetered_customers are nonnegative whole numbers: 0 is recorded zero, blank is unknown.
              </p>
              <p>
                <strong className="text-foreground">IoT installation:</strong>{" "}
                iot_vendor is the actual installer association, not substation ownership or an access tenant.
                iot_phase, iot_installed (true/false), and installation_type_available record installation state.
                source_metadata accepts a quoted JSON object for provenance.
              </p>
              <p>
                Survey history, controller ownership, meter mappings, and configuration publication
                are not part of this CSV import.
              </p>
            </>
          ) : (
            <div className="space-y-3">
              <p>
                <strong className="text-foreground">Required columns:</strong>{" "}
                distribution_company_external_key, external_key, name (or base_name). Create the distribution company
                first. Keep the same substation external key when updating an existing record. Feeders
                are imported separately.
                name is the canonical saved base name; base_name is a compatibility alias.
                Supply the same cleaned base name if both columns are present. Original names
                belong only in the reconciliation workbook and are not stored in the catalog.
              </p>
              <p>
                Edit base_name separately from station_type (TS, ISS, or blank Unknown).
                voltage_kv is the optional numeric station voltage in kV; blank means unknown on creation.
                Generic TCN stations have no inferred station type.
                address_line, city, and state describe the physical station location, not service coverage.
                Optional metadata: served_states (semicolon-separated state names), tcn_region,
                tcn_subregion, transmission_station, injection_substation, latitude, longitude.
                Supply both coordinates or neither. Blank or omitted optional metadata preserves
                existing values on CSV update; use the edit form to clear metadata.
              </p>
            </div>
          )}
          <p>
            Use the downloaded template. Upload one CSV file with a header row. Any invalid row
            rejects the complete file.
          </p>
        </div>
      </details>
    </div>
  );
}
